import React, { useContext, useState, useEffect, useCallback } from 'react';
import { httpsCallable } from "firebase/functions";
import { useFunctions } from "../../helpers/firebaseContext";
import {
  Text,
  Box,
  Card,
  Group,
  Stack,
  Title,
  Button,
  Image,
  Grid,
  Loader,
  Alert,
  Paper,
  SimpleGrid,
  ThemeIcon,
  Center
} from "@mantine/core";
import { IconPhoto, IconIdBadge2, IconUsers, } from '@tabler/icons-react';
import { CustomInfoCard } from '../customers/CustomInfoCard';
import { MyUserDataContext } from "../../contexts";

import { QRCodeVerifyRequest, QRCodeVerifyResult } from '../../types';

interface UrlParams {
  encryptedData: string;
  iv: string;
}

const VerifyQR: React.FC = () => {
  const functions = useFunctions();
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [verificationData, setVerificationData] = useState<QRCodeVerifyResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showNoParams, setShowNoParams] = useState(false);
  const userData = useContext(MyUserDataContext);

  // Extract parameters from URL
  const extractUrlParams = useCallback((): UrlParams | null => {
    const path = window.location.pathname;
    const search = window.location.search;

    // Method 1: Check URL query parameters
    const urlParams = new URLSearchParams(search);
    const encrypted = urlParams.get('qr') || urlParams.get('encryptedData');
    const iv = urlParams.get('data');

    if (encrypted && iv) {
      return { encryptedData: encrypted, iv: iv };
    }

    // Method 2: Check for path-based parameters
    const pathMatch = path.match(/\/qr\/([a-fA-F0-9]+)\/([a-fA-F0-9]+)/);
    if (pathMatch) {
      return {
        encryptedData: pathMatch[1],
        iv: pathMatch[2]
      };
    }

    return null;
  }, []);

  // Main verification function
  const performVerification = useCallback(async (encryptedData: string, iv: string) => {
    try {
      setIsLoading(true);
      setShowResult(false);


      if (!userData) {
        throw new Error("User not authenticated. Please log in and try again.");
      }


      let token = await userData.getAzureADToken!(userData);
      if (!token) {
        if (userData.firebaseTokenResult?.token) {
          token = userData.firebaseTokenResult?.token;
        } else {
          throw new Error("No authentication token available (Microsoft or Firebase). Try refreshing or logging in again.");
        }
      }

      // Validate hex format
      const hexRegex = /^[a-fA-F0-9]+$/;
      if (!hexRegex.test(encryptedData) || !hexRegex.test(iv) || iv.length !== 32) {
        throw new Error('Invalid QR code link');
      }

      const verifyQRCode = httpsCallable<QRCodeVerifyRequest, QRCodeVerifyResult>(functions, "verifyQRCode");
      const result = await verifyQRCode({
        encryptedData,
        iv,
        token: token
      });

      // Success
      setIsLoading(false);
      setIsSuccess(true);
      setVerificationData(result.data);
      setShowResult(true);

    } catch (error: any) {
      console.error('Verification error:', error);

      // Error
      setIsLoading(false);
      setIsSuccess(false);
      setErrorMessage(error.message || 'Failed to verify QR code');
      setShowResult(true);
    }
  }, [functions]);

  // Initialize page
  useEffect(() => {
    const params = extractUrlParams();
    if (params) {
      performVerification(params.encryptedData, params.iv);
    } else {
      setShowNoParams(true);
    }
  }, [extractUrlParams, performVerification]);


  // Format date time helper
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  // Get current date time
  const getCurrentDateTime = () => {
    return new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const handlePrint = () => {
    if (!verificationData) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>KYC - QR Code Verification</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              background: white;
              font-size: 12px;
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
            }
            .print-header img {
              height: 40px;
              width: auto;
            }
            .print-header h1 {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .verification-table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-top: 10px;
            }
            .photo-cell {
              width: 120px;
              height: 150px;
              border: 1px solid #000;
              text-align: center;
              vertical-align: middle;
              padding: 5px;
            }
            .photo-header {
              font-weight: bold;
              font-size: 11px;
              margin-bottom: 5px;
            }
            .user-photo {
              width: 100px;
              height: 120px;
              object-fit: cover;
              border: 1px solid #ccc;
            }
            .data-row {
              height: 25px;
            }
            .label-cell {
              width: 60px;
              border: 1px solid #000;
              padding: 4px 8px;
              background-color: #f0f0f0;
              font-weight: bold;
              font-size: 11px;
              text-align: center;
            }
            .value-cell {
              border: 1px solid #000;
              padding: 4px 8px;
              font-size: 11px;
              text-align: left;
            }
            @page {
              size: A4;
              margin: 1cm;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div>
              <h1>KFH Capital - KYC QR Code Verification</h1>
              <p>Verification Date: ${getCurrentDateTime()}</p>
            </div>
          </div>
          <div class="print-content">
            <h2>KYC PACI Information</h2>
          </div>

          <table class="verification-table">
          <tr>
          <td class="label-cell">Photo</td>
          <td class="photo-cell">
                ${verificationData?.paciData?.photo ?
        `<img src="data:image/jpeg;base64,${verificationData.paciData.photo}" alt="User Photo" class="user-photo">` :
        'No Photo Available'
      }
              </td>
          </tr>
            ${verificationData?.paciData?.fullNameAr ? `
            <tr class="data-row">
              <td class="label-cell">Name Arabic</td>
              <td class="value-cell">${verificationData.paciData.fullNameAr}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.fullNameEn ? `
            <tr class="data-row">
              <td class="label-cell">Name English</td>
              <td class="value-cell">${verificationData.paciData.fullNameEn}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.passportNumber ? `
            <tr class="data-row">
              <td class="label-cell">Passport Number</td>
              <td class="value-cell">${verificationData.paciData.passportNumber}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.nationalityEn ? `
            <tr class="data-row">
              <td class="label-cell">Nationality</td>
              <td class="value-cell">${verificationData.paciData.nationalityEn}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.emailAddress ? `
            <tr class="data-row">
              <td class="label-cell">Email Address</td>
              <td class="value-cell">${verificationData.paciData.emailAddress}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.mobileNumber ? `
            <tr class="data-row">
              <td class="label-cell">Mobile Number</td>
              <td class="value-cell">${verificationData.paciData.mobileNumber}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.civilID ? `
            <tr class="data-row">
              <td class="label-cell">Civil ID</td>
              <td class="value-cell">${verificationData.paciData.civilID}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.civilCardSerial ? `
            <tr class="data-row">
              <td class="label-cell">Civil ID Serial</td>
              <td class="value-cell">${verificationData.paciData.civilCardSerial}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.civilIdExpiryDate ? `
            <tr class="data-row">
              <td class="label-cell">Civil ID Expiry Date</td>
              <td class="value-cell">${formatDateTime(verificationData.paciData.civilIdExpiryDate)}</td>
            </tr>` : ''}

            ${verificationData?.paciData?.transactionDate ? `
            <tr class="data-row">
              <td class="label-cell">Authenticated By PACI</td>
              <td class="value-cell">${formatDateTime(verificationData.paciData.transactionDate)}</td>
            </tr>` : ''}
            <tr class="data-row">
              <td class="label-cell">QR Scan Date</td>
              <td class="value-cell">${getCurrentDateTime()}</td>
            </tr>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  return (
    <Box
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        minHeight: '100vh',
        padding: '2rem 1rem'
      }}
    >
      <Stack gap="xl">
        {/* Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="center">
            <Title order={2} c="#AA9F8C" ta="center">
              KYC - QR Code Verification
            </Title>
          </Group>
        </Card>

        {/* Loading Section */}
        {isLoading && (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Group justify="center" gap="md">
              <Loader size="lg" color="#AA9F8C" />
              <Text c="dimmed" size="lg" fw={500}>
                Verifying QR code...
              </Text>
            </Group>
          </Card>
        )}

        {/* Result Section */}
        {showResult && (
          <>
            {/* Status Alert */}
            <Alert
              color={isSuccess ? "green" : "red"}
              title={isSuccess ? "QR Code Verified Successfully" : "Verification Failed"}
              icon={isSuccess ? "✓" : "✗"}
              radius="md"
              style={{
                background: isSuccess
                  ? 'linear-gradient(135deg, #4CAF50, #45a049)'
                  : 'linear-gradient(135deg, #f44336, #da190b)',
                color: 'white',
                border: 'none'
              }}
            />

            {/* Success Content */}
            {isSuccess && verificationData ? (
              <>
                <SimpleGrid id="verification-grid" cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {/* Client Information */}
                  <CustomInfoCard
                    title="Client Information"
                    color="blue"
                    icon={<IconUsers size={16} />}
                    list={[
                      ...(verificationData?.paciData?.fullNameAr ? [{
                        label: 'Full Name Arabic',
                        value: verificationData.paciData.fullNameAr,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.fullNameEn ? [{
                        label: 'Full Name English',
                        value: verificationData.paciData.fullNameEn,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.emailAddress ? [{
                        label: 'Email Address',
                        value: verificationData.paciData.emailAddress,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.mobileNumber ? [{
                        label: 'Mobile Number',
                        value: verificationData.paciData.mobileNumber,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.passportNumber ? [{
                        label: 'Passport Number',
                        value: verificationData.paciData.passportNumber,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.transactionDate ? [{
                        label: 'Authenticated BY PACI',
                        value: formatDateTime(verificationData.paciData.transactionDate),
                        noCapitalize: true,
                      }] : []),
                      {
                        label: 'QR Scan Date',
                        value: getCurrentDateTime(),
                        noCapitalize: true,
                      }
                    ]}
                  />

                  {/* Civil Information */}
                  <CustomInfoCard
                    title="Civil ID Information"
                    color="orange"
                    icon={<IconIdBadge2 size={16} />}
                    list={[
                      ...(verificationData?.paciData?.civilID ? [{
                        label: 'Civil ID',
                        value: verificationData.paciData.civilID,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.civilCardSerial ? [{
                        label: 'Civil ID Serial',
                        value: verificationData.paciData.civilCardSerial,
                        noCapitalize: true,
                      }] : []),
                      ...(verificationData?.paciData?.civilIdExpiryDate ? [{
                        label: 'Civil ID Expiry Date',
                        value: formatDateTime(verificationData.paciData.civilIdExpiryDate),
                        noCapitalize: true,
                      }] : []),
                    ]}
                  />

                  {/* Photo */}
                  {verificationData?.paciData?.photo && (
                    <Paper shadow="xs" p="md" style={{ height: '100%' }}>
                      <Group mb="xs">
                        <ThemeIcon color="green" variant="light">
                          <IconPhoto size={16} />
                        </ThemeIcon>
                        <Title order={5}>Photo</Title>
                      </Group>
                      <Center>
                        <Image
                          w="auto"
                          fit="contain"
                          h={200}
                          radius="lg"
                          src={`data:image/jpeg;base64,${verificationData.paciData.photo}`}
                          alt="User Photo"
                          style={{
                            border: '2px solid #dee2e6',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Center>
                    </Paper>
                  )}
                </SimpleGrid>

                <Grid w="100%" p="xs">
                  <Button type="button" onClick={async () => handlePrint()}>
                    Print
                  </Button>
                </Grid>
              </>
            ) : (
              /* Error Display */
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Stack align="center" gap="md">
                  <Title order={3} c="red">
                    ⚠️ QR Code Verification Failed
                  </Title>
                  <Text ta="center" c="dimmed">
                    {errorMessage}
                  </Text>
                  <Text>{getCurrentDateTime()}</Text>
                </Stack>
              </Card>

            )}
          </>
        )}

        {/* No Parameters Message */}
        {showNoParams && (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <Title order={3} c="red">
                ⚠️ No QR Code Data Found
              </Title>
              <Text ta="center" c="dimmed">
                This page requires encrypted data and IV parameters in the URL.
              </Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default VerifyQR;